import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { renderMarkdown } from '../lib/markdown'

interface PublicArticle {
  id: string
  slug: string
  title: string
  category_name: string | null
  visibility: string
  updated_at: string
  sections?: Array<{ title: string; content: string | null; children?: Array<{ title: string; content: string | null }> }>
  company_name?: string
}

export default function PublicKBArticle() {
  const { slug } = useParams<{ slug: string }>()
  const [article, setArticle] = useState<PublicArticle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!slug) return
    fetch(`/api/kb/public/${slug}`)
      .then(async res => {
        if (!res.ok) throw new Error('Not found')
        return res.json() as Promise<PublicArticle>
      })
      .then(setArticle)
      .catch(() => setError('Article not found or not public'))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div style={{ maxWidth: 720, margin: '80px auto', padding: '0 24px', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div style={{ height: 32, width: 400, background: '#1c1c2e', borderRadius: 8, marginBottom: 16 }} />
        <div style={{ height: 300, background: '#1c1c2e', borderRadius: 8 }} />
      </div>
    )
  }

  if (error || !article) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0f0f17',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'DM Sans', system-ui, sans-serif", color: '#f1f5f9',
      }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>📄</div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Article not found</h1>
          <p style={{ fontSize: 14, color: '#94a3b8' }}>This article doesn't exist or isn't publicly available.</p>
        </div>
      </div>
    )
  }

  // Build HTML from sections
  const contentHtml = (article.sections || []).map(s => {
    let html = `<h2>${s.title}</h2>`
    if (s.content) {
      html += renderMarkdown(s.content.replace(/\\n/g, '\n'))
    }
    if (s.children) {
      for (const child of s.children) {
        html += `<h3>${child.title}</h3>`
        if (child.content) {
          html += renderMarkdown(child.content.replace(/\\n/g, '\n'))
        }
      }
    }
    return html
  }).join('')

  return (
    <div style={{
      minHeight: '100vh', background: '#0f0f17',
      fontFamily: "'DM Sans', system-ui, sans-serif", color: '#f1f5f9',
    }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid rgba(255,255,255,.06)',
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fbbf24' }}>
          {article.company_name || 'Knowledge Base'}
        </span>
        {article.category_name && (
          <span style={{ fontSize: 12, color: '#64748b' }}>{article.category_name}</span>
        )}
      </header>

      {/* Article */}
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px 80px' }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: '#f1f5f9', marginBottom: 8, lineHeight: 1.3 }}>
          {article.title}
        </h1>

        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 32 }}>
          Last updated {new Date(article.updated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>

        <div
          className="kb-public-content markdown-content"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
      </main>

      <style>{`
        .kb-public-content { font-size: 16px; line-height: 1.8; color: #e2e8f0; }
        .kb-public-content h2 { font-size: 20px; font-weight: 600; margin: 28px 0 10px; color: #f1f5f9; }
        .kb-public-content h3 { font-size: 17px; font-weight: 600; margin: 24px 0 8px; color: #f1f5f9; }
        .kb-public-content p { margin: 0 0 12px; }
        .kb-public-content ul, .kb-public-content ol { padding-left: 24px; margin: 0 0 12px; }
        .kb-public-content li { margin: 4px 0; }
        .kb-public-content blockquote { border-left: 3px solid #fbbf24; padding-left: 16px; margin: 16px 0; color: #94a3b8; font-style: italic; }
        .kb-public-content pre { background: #161622; border: 1px solid rgba(255,255,255,.06); border-radius: 8px; padding: 16px; font-family: 'JetBrains Mono', monospace; font-size: 14px; overflow-x: auto; margin: 16px 0; }
        .kb-public-content code { background: rgba(255,255,255,.06); padding: 2px 6px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 14px; }
        .kb-public-content pre code { background: none; padding: 0; }
        .kb-public-content img { max-width: 100%; border-radius: 8px; margin: 16px 0; }
        .kb-public-content a { color: #fbbf24; text-decoration: underline; text-underline-offset: 2px; }
        .kb-public-content table { border-collapse: collapse; width: 100%; margin: 16px 0; }
        .kb-public-content th, .kb-public-content td { border: 1px solid rgba(255,255,255,.08); padding: 10px 14px; text-align: left; }
        .kb-public-content th { background: rgba(255,255,255,.03); font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8; }
      `}</style>
    </div>
  )
}
